import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  PICKER_SIDE_OFFSET,
  PickerPanel,
  pickerPopoverClassName,
  pickerTriggerOpenClassName,
} from './PickerPanel'
import type { PickerOption } from './PickerPanel'
import type { RichDropdownProps, SelectOption } from '../type'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useIsTouchPicker } from '@/hooks/useMobile'
import { cn } from '@/lib/utils'

/**
 * Picker rows for a SelectOption list: explicit shortcuts win, otherwise the first
 * nine options take 1–9 when numeric shortcuts are on.
 */
export function toPickerOptions(
  options: Array<SelectOption>,
  enableNumericShortcuts: boolean,
): Array<PickerOption> {
  return options.map((option, index) => ({
    value: option.value,
    label: option.labelText,
    icon: option.icon,
    meta: option.meta,
    group: option.group,
    shortcut:
      option.shortcut ??
      (enableNumericShortcuts && index < 9 ? String(index + 1) : undefined),
  }))
}

/**
 * A text-trigger combobox over the shared PickerPanel shell. Supports controlled and
 * uncontrolled open state and optional 1–9 shortcuts.
 *
 * @example
 * ```tsx
 * <ComboBox
 *   options={statusOptions}
 *   defaultSelectedValue={task.status}
 *   onChange={(value) => updateTaskStatus(value)}
 *   placeholderText="Select status"
 *   enableNumericShortcuts
 * />
 * ```
 */
export function ComboBox({
  placeholderText,
  searchText,
  defaultSelectedValue,
  options,
  isDisabled,
  onChange,
  buttonClassName,
  isOpen: controlledOpen,
  onOpenChange,
  triggerProps,
  enableNumericShortcuts = false,
  action,
  triggerContent,
}: RichDropdownProps) {
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null,
  )
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = typeof controlledOpen === 'boolean'
  const isOpen = isControlled ? controlledOpen : internalOpen
  // A tap that selects a row can land on the trigger once the sheet unmounts
  // and open it again. Ignore opens for a moment after a choice (MOL-41).
  const blockOpenRef = useRef(false)
  // A keyboard dismiss can click the trigger long after the sheet closed.
  // Only a pointer that actually started on the trigger may open it (MOL-41).
  const pointerOnTriggerRef = useRef(false)

  const setIsOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && blockOpenRef.current) return
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
    },
    [isControlled, onOpenChange],
  )

  const closeAfterSelect = useCallback(() => {
    blockOpenRef.current = true
    setIsOpen(false)
    setTimeout(() => {
      blockOpenRef.current = false
    }, 350)
  }, [setIsOpen])

  useEffect(() => {
    if (defaultSelectedValue !== '') {
      setSelectedOption(
        options.find((option) => option.value === defaultSelectedValue) || null,
      )
    }
  }, [defaultSelectedValue, options])

  const handleOptionChange = useCallback(
    (value: string) => {
      onChange(value)
      setSelectedOption(
        options.find((option) => option.value === value) || null,
      )
      closeAfterSelect()
    },
    [onChange, options, closeAfterSelect],
  )

  const pickerOptions = useMemo(
    () => toPickerOptions(options, enableNumericShortcuts),
    [options, enableNumericShortcuts],
  )

  const isMobile = useIsTouchPicker()

  const triggerElement = (
    <Button
      variant="outline"
      className={cn('justify-start text-xs h-8', pickerTriggerOpenClassName, buttonClassName)}
      disabled={isDisabled}
      {...triggerProps}
      onPointerDown={(event) => {
        triggerProps?.onPointerDown?.(event)
        pointerOnTriggerRef.current = true
      }}
      onClick={(event) => {
        triggerProps?.onClick?.(event)
        if (!isMobile) return
        if (!pointerOnTriggerRef.current) return
        pointerOnTriggerRef.current = false
        setIsOpen(true)
      }}
    >
      {triggerContent ?? <span className="text-foreground">
        {selectedOption ? (() => {
          const live = options.find((option) => option.value === selectedOption.value)
          return live?.triggerLabel ?? live?.label
        })() : (
          placeholderText
        )}
      </span>}
    </Button>
  )

  const panel = (
    <PickerPanel
      options={pickerOptions}
      selected={selectedOption?.value ?? defaultSelectedValue}
      onSelect={handleOptionChange}
      searchPlaceholder={searchText}
      action={
        action
          ? {
              ...action,
              onSelect: () => {
                closeAfterSelect()
                action.onSelect()
              },
            }
          : undefined
      }
    />
  )

  if (isMobile) {
    // Sheet for a phone or any coarse pointer. A wide tablet is not
    // useIsMobile(), but the desktop popover sits under the keyboard (MOL-41).
    // No DrawerTrigger: a controlled trigger toggles on the same tap that the
    // overlay receives, so the sheet opens and closes forever.
    // repositionInputs / autofocus also resize the Android viewport and
    // dismiss the sheet mid-gesture.
    return (
      <>
        {triggerElement}
        <Drawer open={isOpen} onOpenChange={setIsOpen} direction="bottom" repositionInputs={false} handleOnly>
          <DrawerContent onOpenAutoFocus={(event) => event.preventDefault()}>
            <DrawerTitle className="sr-only">Select an option</DrawerTitle>
            <DrawerDescription className="sr-only">
              Choose an option from the list.
            </DrawerDescription>
            <div className="max-h-[60vh] overflow-y-auto">{panel}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
      <PopoverContent
        className={pickerPopoverClassName}
        align="start"
        sideOffset={PICKER_SIDE_OFFSET}
      >
        {panel}
      </PopoverContent>
    </Popover>
  )
}
